import React from 'react'
import { graphql } from 'gatsby'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import Loadable from '@loadable/component'

import { ActionButton, Footer, FormField, GtmDataLayerSetup, HeaderBanner, Link, Title } from '@jelly/components'
import { makeImgResizerUrl } from '@jelly/utils'
import Layout from 'common/LayoutNoHero'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

const Form = Loadable(() => import(/* webpackChunkName: 'rff' */ 'react-final-form').then(({ Form }) => Form))

const Container = styled.div`
	${({ theme }) => theme.background.secondary};
`

const Text = styled.p`
	${({ theme, variant }) => theme.text[variant]};
`
Text.defaultProps = {
	variant: 'bodyLarge',
}

const HeaderBannerWrap = styled.div`
	background: ${({ theme }) => theme.colors.lightGrey};
	width: 100%;
	border-bottom: ${({ theme }) => `thin solid ${theme.colors.navy}`};
`
// Styled Components via Emotion
const BodyContainer = styled.div`
	height: 100%;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		max-width: 60vw;
		width: 90%;
		min-height: 70vh;
		margin: auto;
		margin-top: 10px;
		margin-bottom: 30px;
	}
`
const Tile = styled.div`
	background-color: ${({ theme }) => theme.colors.white};
	padding: 1rem;
	margin-bottom: 1rem;
	box-shadow: ${({ theme }) => theme.shadows.small};
`

const Separator = styled.span`
	margin: 0 10px;
`

const Links = styled.div`
	padding-bottom: 1rem;
	display: flex;
	flex-direction: row;
`

const FormWrapper = styled.form`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	> * {
		margin: 1rem;
	}
`
const Card = styled.div`
	display: flex;
	background-color: ${({ theme }) => theme.colors.white};
	box-shadow: ${({ theme }) => theme.shadows.small};
	margin: 2rem 0;
	padding: 1rem;
`
const Col = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: space-between;
	> img {
		margin-right: 1rem;
	}
	> p,
	> a {
		margin-top: 0;
		margin-bottom: 1rem;
	}
`
const Article = ({ author, canonical, href, published, stream, thumbnail, title }) => {
	return (
		<Card>
			<Col>
				<img src={makeImgResizerUrl({ method: 'rc', src: thumbnail.path, w: 150, h: 130 })} alt={href} />
			</Col>
			<Col>
				<Link variant="default" href={canonical}>
					{title}
				</Link>
				<Text variant="bodySmall">{stream}</Text>
				<Text>
					{`By ${author.displayName}`}
					&nbsp;-&nbsp;{dayjs().to(dayjs(published))}
				</Text>
			</Col>
		</Card>
	)
}

const NotFoundPage = ({ data, message = 'Sorry, we made a mistake.' }) => {
	return (
		<Layout meta={{ title: 'Page not found - business.com' }}>
			<Container>
				<GtmDataLayerSetup contentType="404" templateType="" />
				<HeaderBannerWrap>
					<HeaderBanner />
				</HeaderBannerWrap>
				<BodyContainer>
					<Tile>
						<div class="tile-content copy">
							<div class="block pb2">
								<Title variant="sectionHeaderLarge">{message}</Title>
								<Text>
									We couldn't find the page you were looking for (that's on us). Let us help you find
									the right resource for your business. Please choose one of the options below or
									email us at &nbsp;
									<Link href="mailto:connect@business.com?subject=Page Not Found">
										connect@business.com
									</Link>
									.
								</Text>
								<Links>
									<Link href="/content/">Browse Articles</Link>
									<Separator>|</Separator>
									<Link href="/advice/answers">Seek Advice</Link>
									<Separator>|</Separator>
									<Link href="/reviews/">Find Reviews</Link>
								</Links>
							</div>
						</div>
					</Tile>
					<Tile>
						<Title variant="sectionHeaderSmall">What can we help you find?</Title>
						<Form
							onSubmit={({ q }) => {
								window.location.href = `/search?q=${q}`
							}}
							render={({ handleSubmit, form, submitting, pristine, values }) => (
								<FormWrapper onSubmit={handleSubmit}>
									<FormField type="text" name="q" required />
									<ActionButton variant="small" type="submit">
										Submit
									</ActionButton>
								</FormWrapper>
							)}
						/>
					</Tile>
					<Title variant="sectionHeaderSmall">Latest Content:</Title>
					{data.articles.map((article) => (
						<Article {...article} />
					))}
				</BodyContainer>
				<Footer />
			</Container>
		</Layout>
	)
}

export default NotFoundPage

export const GATSBY = graphql`
	query {
		articles(limit: 10) {
			href
			author {
				displayName
			}
			canonical
			published
			subtitle
			stream
			thumbnail {
				path
				caption
			}
			title
		}
	}
`
